import { useCallback, useEffect, useRef, useState } from 'react';
import { chatAPI, issuesAPI } from '../../services/api';

/* The conversation and the stream that feeds it.
 *
 * Kept in sessionStorage (one key per signed-in user): it survives page changes and a
 * reload, but not closing the tab, so tomorrow's chat does not open on yesterday's
 * "what needs attention today" answer, and a shared phone does not hand the next
 * person the last one's conversation.
 *
 * Message shapes:
 *   { id, role: 'user', content, issue?: {id, title}, context? }
 *     `issue` is an issue tagged with the question; `context` is the server's reading of it
 *     (trail + what every photo shows), sent back in front of the question with each
 *     follow-up so the photos are read once.
 *   { id, role: 'assistant', content, steps: [{tool, label, at}], photos: [{plant_id, images}],
 *     status: 'streaming' | 'done' | 'error' | 'stopped', error?, model? }
 * `at` is how much answer text existed when the step began, which tells the trail
 * whether the assistant is still on that step or has moved on to writing. */

const STORE = 'nk.chat.v1';
const MAX_STORED = 60;       // messages kept across reloads
const HISTORY_TURNS = 12;    // turns sent with each question (text only)

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

function load(owner) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORE) || 'null');
    if (!saved || saved.owner !== owner || !Array.isArray(saved.messages)) return [];
    // A reload mid-answer leaves a 'streaming' message nobody is feeding any more.
    return saved.messages.map(m => (m.status === 'streaming'
      ? { ...m, status: 'error', error: 'The page reloaded before this answer finished.' }
      : m));
  } catch (e) {
    return [];
  }
}

export function describeError(err) {
  if (err?.status === 503) return 'The assistant is not set up on this server yet.';
  if (err?.status === 401) return 'You have been signed out. Sign in again to keep asking.';
  if (err?.status === 400) return err.detail || 'The server could not read that question.';
  if (err?.status === 429) return err.detail || 'Too many questions at once. Wait a moment and try again.';
  if (err?.status) return err.detail || `The server failed to answer (HTTP ${err.status}). Try again.`;
  if (err instanceof TypeError) return 'Could not reach the server. Check the connection and try again.';
  return err?.message || 'Something went wrong while answering.';
}

export default function useChat(owner) {
  const [messages, setMessages] = useState(() => load(owner));
  const [busy, setBusy] = useState(false);
  const messagesRef = useRef(messages);
  const abortRef = useRef(null);
  messagesRef.current = messages;

  // Switching user in the same tab (sign out, sign in as someone else) starts clean.
  const ownerRef = useRef(owner);
  useEffect(() => {
    if (ownerRef.current === owner) return;
    ownerRef.current = owner;
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
    setMessages(load(owner));
  }, [owner]);

  // Save, but not on every streamed chunk: a short debounce writes once things settle.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        if (!messages.length) sessionStorage.removeItem(STORE);
        else sessionStorage.setItem(STORE, JSON.stringify({ owner, messages: messages.slice(-MAX_STORED) }));
      } catch (e) { /* storage full or blocked: the chat still works for this page */ }
    }, busy ? 800 : 0);
    return () => clearTimeout(t);
  }, [messages, busy, owner]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback((text, base, issue) => {
    const content = String(text || '').trim();
    if (!content || abortRef.current) return false;
    const prior = base || messagesRef.current;

    const history = prior
      .filter(m => (m.role === 'user' || m.role === 'assistant') && m.content && m.content.trim())
      .slice(-HISTORY_TURNS)
      .map(m => ({ role: m.role, content: m.context ? `${m.context}\n\n${m.content}` : m.content }));

    const question = { id: uid(), role: 'user', content, ...(issue && { issue }) };
    const answer = { id: uid(), role: 'assistant', content: '', steps: [], photos: [], status: 'streaming' };
    setMessages([...prior, question, answer]);

    const patch = fn => setMessages(ms => ms.map(m => (m.id === answer.id ? fn(m) : m)));
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setBusy(true);
    let ended = false;

    const onEvent = (ev) => {
      switch (ev.type) {
        case 'status':
          patch(m => ({ ...m, steps: [...m.steps, { tool: ev.tool, label: ev.label || ev.tool || 'Checking the records', at: m.content.length }] }));
          break;
        case 'text':
          if (ev.delta) patch(m => ({ ...m, content: m.content + ev.delta }));
          break;
        case 'context':
          if (ev.text) setMessages(ms => ms.map(m => (m.id === question.id ? { ...m, context: ev.text } : m)));
          break;
        case 'images': {
          // Issue photos are served under the API prefix (/api/issues/... in production).
          const src = p => (p && p.startsWith('/issues/') ? issuesAPI.src(p) : p);
          const images = (ev.images || []).filter(i => i && (i.thumb || i.full))
            .map(i => ({ thumb: src(i.thumb), full: src(i.full) }));
          if (!images.length) break;
          const key = ev.label || ev.plant_id || null;
          patch((m) => {
            const photos = [...m.photos];
            const at = photos.findIndex(p => (p.label || p.plant_id || null) === key);
            if (at >= 0) {
              const seen = new Set(photos[at].images.map(i => i.full || i.thumb));
              photos[at] = { ...photos[at], images: [...photos[at].images, ...images.filter(i => !seen.has(i.full || i.thumb))] };
            } else photos.push({ plant_id: ev.plant_id || null, label: ev.label || null, images });
            return { ...m, photos };
          });
          break;
        }
        case 'done':
          ended = true;
          patch(m => ({ ...m, status: 'done', model: ev.model || null }));
          break;
        case 'error':
          ended = true;
          patch(m => ({ ...m, status: 'error', error: ev.error || 'The assistant stopped with an error.' }));
          break;
        default:
      }
    };

    chatAPI.streamMessage(content, history, { signal: ctrl.signal, onEvent, issueId: issue?.id })
      .then(() => {
        if (!ended) patch(m => ({ ...m, status: 'error', error: 'The connection closed before the answer finished.' }));
      })
      .catch((err) => {
        if (err?.name === 'AbortError') patch(m => (m.status === 'streaming' ? { ...m, status: 'stopped' } : m));
        else patch(m => ({ ...m, status: 'error', error: describeError(err) }));
      })
      .finally(() => {
        // Only the stream that is still current may clear the busy flag: after New chat
        // a fresh question can already be running when the old one's promise settles.
        if (abortRef.current === ctrl) {
          abortRef.current = null;
          setBusy(false);
        }
      });
    return true;
  }, []);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  // Ask the question behind an answer again, dropping that answer (and anything after it).
  const retry = useCallback((answerId) => {
    if (abortRef.current) return;
    const ms = messagesRef.current;
    const at = ms.findIndex(m => m.id === answerId);
    if (at < 1 || ms[at - 1].role !== 'user') return;
    send(ms[at - 1].content, ms.slice(0, at - 1), ms[at - 1].issue);
  }, [send]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
    setMessages([]);
  }, []);

  return { messages, busy, send, stop, retry, reset };
}
